import type { Logger as PinoLogger } from "pino"

// Server-side check
const isServer = typeof window === "undefined"
const isProduction = process.env.NODE_ENV === "production"

// OpenTelemetry endpoint for SigNoz (optional - logs still go to stdout if not set)
const otelEndpoint = process.env.OTEL_EXPORTER_OTLP_ENDPOINT

// Sensitive fields to redact
const redactPaths = [
   "password",
   "*.password",
   "token",
   "*.token",
   "authorization",
   "*.authorization",
   "cookie",
   "*.cookie",
   "apiKey",
   "*.apiKey",
   "secret",
   "*.secret",
   "credentials",
   "*.credentials",
   "creditCard",
   "*.creditCard",
   "auth_token",
   "*.auth_token",
   "session",
   "*.session",
   // raauth token/secret fields (not covered by the generic keys above)
   "access_token",
   "*.access_token",
   "refresh_token",
   "*.refresh_token",
   "id_token",
   "*.id_token",
   "client_secret",
   "*.client_secret",
   "reset_token",
   "*.reset_token",
   "logout_token",
   "*.logout_token",
]

// Type for the logger interface
type LogFn = (obj: any, msg?: string) => void
interface Logger {
   debug: LogFn
   info: LogFn
   warn: LogFn
   error: LogFn
   child: (context: Record<string, any>) => Logger
}

// Simple client-side logger that mimics pino interface
const createClientLogger = (): Logger => {
   const logLevel = process.env.LOG_LEVEL || (isProduction ? "info" : "debug")
   const levels = ["debug", "info", "warn", "error"]
   const currentLevelIndex = levels.indexOf(logLevel)

   const shouldLog = (level: string) => levels.indexOf(level) >= currentLevelIndex

   const log = (level: string, obj: any, msg?: string) => {
      if (!shouldLog(level)) return
      const method = level === "debug" ? "log" : level === "info" ? "log" : level
      if (typeof obj === "string") {
         console[method as "log" | "warn" | "error"](`[${level.toUpperCase()}]`, obj)
      } else {
         console[method as "log" | "warn" | "error"](`[${level.toUpperCase()}]`, msg || "", obj)
      }
   }

   const createLogger = (baseContext: Record<string, any> = {}): Logger => ({
      debug: (obj: any, msg?: string) => log("debug", { ...baseContext, ...obj }, msg),
      info: (obj: any, msg?: string) => log("info", { ...baseContext, ...obj }, msg),
      warn: (obj: any, msg?: string) => log("warn", { ...baseContext, ...obj }, msg),
      error: (obj: any, msg?: string) => log("error", { ...baseContext, ...obj }, msg),
      child: (context: Record<string, any>) => createLogger({ ...baseContext, ...context }),
   })

   return createLogger({ service: "restaurant-association" })
}

// Build production transport configuration
const buildProductionTransport = () => {
   // Base transport: always output to stdout (JSON)
   const targets: { target: string; options: Record<string, any> }[] = [
      { target: "pino/file", options: { destination: 1 } },
   ]

   // If OTEL endpoint is configured, also send to SigNoz
   if (otelEndpoint) {
      targets.push({
         target: "pino-opentelemetry-transport",
         options: {
            loggerName: "restaurant-association",
            resourceAttributes: {
               "service.name": "restaurant-association",
               "deployment.environment": process.env.NODE_ENV || "production",
            },
         },
      })
   }

   return { targets }
}

// Create a synchronous logger that works on both client and server
let _logger: Logger

if (isServer) {
   // On server, use Pino
   const pino = require("pino")

   // Base config without formatters (not supported with multi-transport)
   const baseConfig = {
      level: process.env.LOG_LEVEL || (isProduction ? "info" : "debug"),
      redact: {
         paths: redactPaths,
         censor: "[REDACTED]",
      },
      base: {
         service: "restaurant-association",
         env: process.env.NODE_ENV,
      },
      timestamp: pino.stdTimeFunctions.isoTime,
   }

   // Development config with formatters (single transport supports it)
   const devConfig = {
      ...baseConfig,
      formatters: {
         level: (label: string) => ({ level: label }),
      },
      transport: {
         target: "pino-pretty",
         options: {
            colorize: true,
            translateTime: "SYS:standard",
            ignore: "pid,hostname",
         },
      },
   }

   // Production config (multi-transport doesn't support formatters)
   const prodConfig = {
      ...baseConfig,
      transport: buildProductionTransport(),
   }

   const pinoLogger: PinoLogger = pino(isProduction ? prodConfig : devConfig)

   _logger = pinoLogger as unknown as Logger
} else {
   // On client, use simple console logger
   _logger = createClientLogger()
}

export const logger = _logger

// Child loggers for different modules
export const authLogger = logger.child({ module: "auth" })
export const apiLogger = logger.child({ module: "api" })
export const bulkImportLogger = logger.child({ module: "bulk-import" })
export const dbLogger = logger.child({ module: "database" })
export const videoLogger = logger.child({ module: "video" })
export const articleLogger = logger.child({ module: "article" })
export const raauthLogger = logger.child({ module: "raauth" })
